import React, { useEffect, useState } from 'react';
import { getTemples, addTemple, updateTemple, deleteTemple, uploadFileToServer } from '../services/api';
import { PlusCircle, Search, Edit3, Trash2, Landmark, MapPin, Clock, Sunset, Sunrise } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const ManageTemples = () => {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  
  const initialFormState = {
    name: '',
    slug: '',
    description: '',
    full_description: '',
    location: '',
    morning_timing: '',
    evening_timing: '',
    images: [''],
    map_url: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    seo_image: ''
  };
  const [formData, setFormData] = useState(initialFormState);
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);

  const fetchTemples = async () => {
    setLoading(true);
    const data = await getTemples();
    setTemples(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchTemples();
  }, []);

  const handleEdit = (temple) => {
    setFormData({
      name: temple.name || '',
      slug: temple.slug || '',
      description: temple.description || '',
      full_description: temple.full_description ? temple.full_description.replace(/&nbsp;/g, ' ') : '',
      location: temple.location || '',
      morning_timing: temple.morning_timing || '',
      evening_timing: temple.evening_timing || '',
      images: temple.images?.length ? temple.images : (temple.image ? [temple.image] : ['']),
      map_url: temple.map_url || '',
      seo_title: temple.seo_title || '',
      seo_description: temple.seo_description || '',
      seo_keywords: temple.seo_keywords || '',
      seo_image: temple.seo_image || ''
    });
    setCurrentId(temple.id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if(window.confirm('Are you sure you want to delete this temple? This action cannot be undone.')) {
      await deleteTemple(id);
      fetchTemples();
    }
  };

  const handleAddImage = () => {
    if (formData.images.length < 6) {
      setFormData({ ...formData, images: [...formData.images, ''] });
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    if(newImages.length === 0) newImages.push('');
    setFormData({ ...formData, images: newImages });
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const handleFileChange = async (index, file) => {
    if (!file) return;
    setUploadingImageIndex(index);
    const result = await uploadFileToServer(file);
    if (result.success) {
      handleImageChange(index, result.url);
    } else {
      alert("Failed to upload image: " + result.message);
    }
    setUploadingImageIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanedFullDesc = formData.full_description ? formData.full_description.replace(/&nbsp;/g, ' ') : '';
    const cleanedData = { ...formData, full_description: cleanedFullDesc, images: formData.images.filter(img => img.trim() !== '') };
    if (currentId) {
      await updateTemple(currentId, cleanedData);
    } else {
      await addTemple(cleanedData);
    }
    
    setIsEditing(false);
    setCurrentId(null);
    setFormData(initialFormState);
    fetchTemples();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentId(null);
    setFormData(initialFormState);
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8 pt-2">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">Manage Temples</h1>
          <p className="text-stone-500">Add, edit, and organize temple information and details.</p>
        </div>
        <button 
          onClick={() => {
            if (isEditing && currentId) {
              handleCancel();
            } else {
              setIsEditing(!isEditing);
            }
          }}
          className={`inline-flex items-center px-4 py-2.5 font-medium rounded-xl transition ${isEditing ? 'bg-stone-200 text-stone-800 hover:bg-stone-300' : 'bg-saffron-600 text-white hover:bg-saffron-700'}`}
        >
          {isEditing ? 'Cancel Edit' : <><PlusCircle className="w-5 h-5 mr-2" /> Add New Temple</>}
        </button>
      </div>

      {isEditing && (
        <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-8 border-l-4 border-l-saffron-500 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
          <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center">
            <Landmark className="w-5 h-5 text-saffron-500 mr-2" />
            {currentId ? 'Edit Temple Details' : 'Create New Temple'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Temple Name</label>
                <input required value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500 outline-none transition" placeholder="e.g. Alakhnath Temple" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">URL Slug <span className="text-xs font-normal text-stone-400">(Optional)</span></label>
                <input value={formData.slug} onChange={e=>setFormData({...formData, slug: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 focus:border-saffron-500 outline-none transition" placeholder="e.g. alakhnath-temple" />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Location</label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-stone-400 absolute left-3 top-3.5" />
                  <input required value={formData.location} onChange={e=>setFormData({...formData, location: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 pl-9 outline-none focus:border-saffron-500 transition" placeholder="e.g. Kila Road, Bareilly" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Morning Timing</label>
                <div className="relative">
                  <Sunrise className="w-4 h-4 text-stone-400 absolute left-3 top-3.5" />
                  <input required value={formData.morning_timing} onChange={e=>setFormData({...formData, morning_timing: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 pl-9 outline-none focus:border-saffron-500 transition" placeholder="e.g. 5:00 AM - 12:00 PM" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Evening Timing</label>
                <div className="relative">
                  <Sunset className="w-4 h-4 text-stone-400 absolute left-3 top-3.5" />
                  <input required value={formData.evening_timing} onChange={e=>setFormData({...formData, evening_timing: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 pl-9 outline-none focus:border-saffron-500 transition" placeholder="e.g. 4:00 PM - 9:00 PM" />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-stone-700 mb-1">Google Maps URL</label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-stone-400 absolute left-3 top-3.5" />
                  <input type="url" value={formData.map_url} onChange={e=>setFormData({...formData, map_url: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 pl-9 outline-none focus:border-saffron-500 transition" placeholder="e.g. https://maps.google.com/..." />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-stone-700">Temple Images (Max 6)</label>
              <p className="text-xs text-stone-400 mb-2">The first image will be used as the main cover image.</p>
              {formData.images.map((img, index) => (
                <div key={index} className="flex space-x-2 items-start">
                  <div className="flex-1">
                    <input value={img} onChange={e=>handleImageChange(index, e.target.value)} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder={`Image URL ${index + 1}`} required={index === 0} />
                    <div className="mt-2 flex border-t border-transparent pt-1 items-center justify-between">
                      <div className="text-xs text-saffron-600 truncate flex items-center">
                        {img && <img src={img} alt="Preview" className="w-8 h-8 object-cover rounded mr-2" onError={(e) => { e.target.onerror = null; e.target.src="https://via.placeholder.com/40?text=Error" }} />}
                        {img && `Preview ${index + 1}`}
                      </div>
                      <label className="cursor-pointer bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs px-3 py-1.5 rounded border border-stone-300 transition-colors flex items-center">
                         {uploadingImageIndex === index ? "Uploading..." : "Browse File"}
                         <input type="file" className="hidden" accept="image/*" onChange={(e)=>handleFileChange(index, e.target.files[0])} disabled={uploadingImageIndex === index} />
                      </label>
                    </div>
                  </div>
                  {formData.images.length > 1 && (
                    <button type="button" onClick={() => handleRemoveImage(index)} className="mt-1 p-2 text-stone-400 hover:text-red-500 bg-stone-100 hover:bg-stone-200 rounded-lg transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {formData.images.length < 6 && (
                <button type="button" onClick={handleAddImage} className="text-sm font-medium text-saffron-600 hover:text-saffron-700 flex items-center">
                  <PlusCircle className="w-4 h-4 mr-1" /> Add Another Image
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Short Description (Excerpt)</label>
              <textarea required rows={2} value={formData.description} onChange={e=>setFormData({...formData, description: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition resize-none" placeholder="Brief summary for cards..."></textarea>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-stone-700 mb-1">Full Description</label>
              <ReactQuill theme="snow" value={formData.full_description} onChange={(val)=>setFormData({...formData, full_description: val})} className="bg-white rounded-b-lg border-stone-300 pb-12 h-64" />
            </div>

            {/* SEO SECTION */}
            <div className="mt-8 pt-6 border-t border-stone-200">
              <h3 className="text-lg font-bold text-stone-800 mb-4 flex items-center">
                Search Engine Optimization 
                <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-saffron-100 text-saffron-700 rounded-full">Optional</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Title</label>
                  <input value={formData.seo_title} onChange={e=>setFormData({...formData, seo_title: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder="e.g. Alakhnath Temple - Explore Bareilly..." />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Keywords</label>
                  <input value={formData.seo_keywords} onChange={e=>setFormData({...formData, seo_keywords: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder="e.g. alakhnath, hindu temple, shiva, bareilly" />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Description</label>
                <textarea rows={2} value={formData.seo_description} onChange={e=>setFormData({...formData, seo_description: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition resize-none" placeholder="Brief metadata description for Google search results..."></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Social Thumbnail</label>
                <div className="flex space-x-2 items-start">
                  <div className="flex-1">
                    <input value={formData.seo_image} onChange={e=>setFormData({...formData, seo_image: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder="Image URL for Facebook/WhatsApp sharing" />
                    <div className="mt-2 flex border-t border-transparent pt-1 items-center justify-between">
                      <div className="text-xs text-saffron-600 truncate flex items-center">
                        {formData.seo_image && <img src={formData.seo_image} alt="SEO Preview" className="w-8 h-8 object-cover rounded mr-2" />}
                        {formData.seo_image && `Preview SEO Thumbnail`}
                      </div>
                      <label className="cursor-pointer bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs px-3 py-1.5 rounded border border-stone-300 transition-colors flex items-center">
                          Browse File
                         <input type="file" className="hidden" accept="image/*" onChange={async (e) => {
                            if(e.target.files[0]) {
                              const res = await uploadFileToServer(e.target.files[0]);
                              if(res.success) setFormData({...formData, seo_image: res.url});
                            }
                         }} />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button type="submit" className="px-8 py-3 text-white bg-saffron-600 hover:bg-saffron-700 rounded-xl font-bold transition shadow-md shadow-saffron-600/20">
                {currentId ? 'Save Changes' : 'Publish Temple'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3,4].map(i => <div key={i} className="h-20 bg-stone-100 rounded-2xl w-full"></div>)}
        </div>
      ) : (
        <div className="overflow-hidden border border-stone-200 rounded-2xl bg-white shadow-sm">
          <table className="min-w-full divide-y divide-stone-200">
            <thead className="bg-stone-50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Temple Details</th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider hidden md:table-cell">Location & Info</th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-stone-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-200">
              {temples.map((temple) => (
                <tr key={temple.id} className="hover:bg-stone-50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-16">
                        <img className="h-12 w-16 rounded-lg object-cover border border-stone-200" src={temple.image} alt={temple.name} onError={(e)=>{e.target.src="https://via.placeholder.com/64?text=No+Img"}} />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-stone-900 group-hover:text-saffron-600 transition-colors">{temple.name}</div>
                        <div className="text-xs text-stone-500 line-clamp-1 max-w-xs">{temple.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">
                    <div className="text-sm text-stone-900 flex items-center mb-1"><MapPin className="w-3 h-3 text-stone-400 mr-1"/> {temple.location}</div>
                    <div className="text-xs text-stone-500 flex items-center mb-1"><Sunrise className="w-3 h-3 text-stone-400 mr-1"/> {temple.morning_timing}</div>
                    <div className="text-xs text-stone-500 flex items-center"><Sunset className="w-3 h-3 text-stone-400 mr-1"/> {temple.evening_timing}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => handleEdit(temple)}
                      className="text-stone-400 hover:text-saffron-600 bg-stone-100 hover:bg-saffron-50 p-2 rounded-lg transition-colors mr-2"
                      title="Edit Temple"
                    >
                      <Edit3 className="w-4 h-4"/>
                    </button>
                    <button 
                      onClick={() => handleDelete(temple.id)}
                      className="text-stone-400 hover:text-red-600 bg-stone-100 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      title="Delete Temple"
                    >
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </td>
                </tr>
              ))}
              
              {temples.length === 0 && (
                <tr>
                  <td colSpan="3" className="px-6 py-12 text-center text-stone-500">
                    <Landmark className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                    <p className="font-medium text-stone-800">No temples found.</p>
                    <p className="text-sm">Click "Add New Temple" to get started.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageTemples;
